import { useState } from 'react';
import type { ButtonProps as AriaButtonProps, PressEvent } from 'react-aria-components';
import {
  Button as AriaButton,
  composeRenderProps,
} from 'react-aria-components';

import type { ButtonBaseProps } from './utils.ts';
import { buttonStyles } from './utils.ts';

type Props = AriaButtonProps & ButtonBaseProps & {
  action?: (e: PressEvent) => void | Promise<void>;
};

export function Button(props: Props) {
  const [isPending, setIsPending] = useState(false);
  const action: Props['action'] = async (e) => {
    if (!props.action) {
      return;
    }

    setIsPending(true);
    try {
      await props.action(e);
    }
    finally {
      setIsPending(false);
    }
  };

  return (
    <AriaButton
      isPending={isPending}
      {...props}
      onPress={(...args) => {
        void action(...args);
      }}
      className={composeRenderProps(
        props.className,
        (className, renderProps) => buttonStyles({
          ...renderProps,
          color: props.color,
          variant: props.variant,
          size: props.size,
          isActive: props.isActive || renderProps.isPressed,
          className,
        }),
      )}
    >
      {composeRenderProps(props.children, (children, { isPending }) => (
        <>
          {!isPending
            ? props.icon?.component
            : (
                <span aria-hidden className="flex inset-0 justify-center items-center">
                  <svg className="w-4 h-4 text-current animate-spin motion-reduce:animate-none" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" className="opacity-25" />
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" fill="none" pathLength="100" strokeDasharray="60 140" strokeDashoffset="0" />
                  </svg>
                </span>
              )}
          {children}
        </>
      ))}
    </AriaButton>
  );
}
